import React, { useState } from 'react';
import '../CSS/ProductOverlay.css';
import { getItemImageUrl } from '../utils/subdomainUtils';

function ProductOverlay({ item, onClose, onAddToCart, menuImages }) {
  // Safely get available sizes or provide defaults
  const availableSizes = item.sizes || { regular: { price_diff: 0 } };
  const defaultSize = Object.keys(availableSizes)[0] || 'regular';
  
  // Detect if options are sizes or protein types
  const sizeKeys = Object.keys(availableSizes);
  const isProteinOptions = sizeKeys.some(key => 
    ['chicken', 'steak', 'shrimp', 'beef', 'pork', 'fish', 'trio', 'vegetarian', 'vegan'].includes(key.toLowerCase())
  );
  const isSizeOptions = sizeKeys.some(key => 
    ['small', 'medium', 'large', 'regular', 'extra-large', 'xl'].includes(key.toLowerCase())
  );
  
  // Determine section title
  const getSectionTitle = () => {
    if (isProteinOptions && !isSizeOptions) {
      return 'Select Protein';
    } else if (isSizeOptions && !isProteinOptions) {
      return 'Select Size';
    } else if (isProteinOptions && isSizeOptions) {
      return 'Select Option';
    } else {
      return 'Select Size';
    }
  };
  
  const [selectedSize, setSelectedSize] = useState(defaultSize);
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [notes, setNotes] = useState('');
  const [price, setPrice] = useState(item.base_price || 0);

  // Use the centralized image URL function

  const handleSizeSelect = (size) => {
    setSelectedSize(size);
    updatePrice(size, selectedAddOns);
  };

  const handleAddOnToggle = (addOn) => {
    let newAddOns;
    if (selectedAddOns.includes(addOn.name)) {
      newAddOns = selectedAddOns.filter(name => name !== addOn.name);
    } else {
      newAddOns = [...selectedAddOns, addOn.name];
    }
    setSelectedAddOns(newAddOns);
    updatePrice(selectedSize, newAddOns);
  };

  const updatePrice = (size, addOns) => {
    let total = item.base_price || 0;
    
    // Add size price difference (safely)
    if (availableSizes[size] && availableSizes[size].price_diff) {
      total += availableSizes[size].price_diff;
    }
    
    // Add selected add-ons (safely)
    if (item.add_ons && Array.isArray(item.add_ons)) {
      addOns.forEach(addOnName => {
        const addOn = item.add_ons.find(a => a.name === addOnName);
        if (addOn && addOn.price) {
          total += addOn.price;
        }
      });
    }
    
    setPrice(total);
  };

  const handleAddToCart = () => {
    // Get the full size object with name and price difference (safely)
    const selectedSizeObj = {
      name: selectedSize,
      price: (availableSizes[selectedSize] && availableSizes[selectedSize].price_diff) || 0
    };

    // Get the full add-on objects (safely)
    const selectedAddOnsObj = item.add_ons && Array.isArray(item.add_ons) 
      ? selectedAddOns.map(addOnName => 
          item.add_ons.find(addOn => addOn.name === addOnName)
        ).filter(Boolean) // Remove any undefined items
      : [];
    
    // Calculate total price including size and add-ons (safely)
    const sizePrice = (availableSizes[selectedSize] && availableSizes[selectedSize].price_diff) || 0;
    const addOnsPrice = selectedAddOnsObj.reduce((sum, addOn) => sum + (addOn.price || 0), 0);
    const totalPrice = (item.base_price || 0) + sizePrice + addOnsPrice;
    
    onAddToCart({
      ...item,
      selectedSize: selectedSizeObj,
      selectedAddOns: selectedAddOnsObj,
      notes: notes,
      quantity: 1,
      totalPrice: totalPrice
    });
  };

  return (
    <div className="product-overlay">
      <div className="overlay-content">
        {/* Left Column */}
        <div className="product-info">
          <h2 className="product-title">{item.item_name}</h2>
          <div className="product-image">
            <img src={getItemImageUrl(item.item_name, item.category, menuImages)} alt={item.item_name} />
          </div>
          <div className="product-description">
            {item.description}
          </div>
          
          {/* Nutrition Information Card */}
          <div className="nutrition-card">
            <h3>Nutrition Information</h3>
            <div className="nutrition-content">
              <p className="calories">Calories: {item.calories || 'N/A'}</p>
              {item.macros && (
                <div className="macros-container">
                  <div className="macro-item">
                    <span className="macro-label">Protein</span>
                    <span className="macro-value">{item.macros.protein || 0}g</span>
                  </div>
                  <div className="macro-item">
                    <span className="macro-label">Carbs</span>
                    <span className="macro-value">{item.macros.carbs || 0}g</span>
                  </div>
                  <div className="macro-item">
                    <span className="macro-label">Fats</span>
                    <span className="macro-value">{item.macros.fats || 0}g</span>
                  </div>
                </div>
              )}
              {item.health_benefits && (
                <div className="health-benefits">
                  <div className="reason-header">
                    <span className="ai-icon">💪</span>
                    <span>Health Benefits</span>
                  </div>
                  <div className="benefits-content">
                    {item.health_benefits}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="customization-panel">
          {/* Size Selection */}
          <div className="size-selection">
            <h3>{getSectionTitle()}</h3>
            <div className="size-options">
              {Object.entries(availableSizes).map(([size, details]) => (
                <button
                  key={size}
                  className={`size-option ${selectedSize === size ? 'selected' : ''}`}
                  onClick={() => handleSizeSelect(size)}
                >
                  {size.charAt(0).toUpperCase() + size.slice(1)}
                  {details.price_diff !== undefined && details.price_diff > 0 && (
                    <span className="size-price">+${details.price_diff.toFixed(2)}</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Add-ons Selection */}
          {item.add_ons && item.add_ons.length > 0 && (
            <div className="add-ons-section">
              <h3>Add-ons</h3>
              <div className="add-ons-options">
                {item.add_ons.map((addOn) => (
                  <button
                    key={addOn.name}
                    className={`add-on-option ${selectedAddOns.includes(addOn.name) ? 'selected' : ''}`}
                    onClick={() => handleAddOnToggle(addOn)}
                  >
                    {addOn.name}
                    <span className="add-on-price">+${addOn.price.toFixed(2)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Notes Input */}
          <div className="notes-section">
            <h3>Special Instructions</h3>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any special instructions here..."
              rows={4}
            />
          </div>

          {/* Price Display */}
          <div className="price-display">
            <h3>Total Price</h3>
            <div className="price">${price.toFixed(2)}</div>
          </div>

          {/* Add to Cart Button */}
          <button 
            className="add-to-cart-btn"
            onClick={handleAddToCart}
          >
            Add to Cart
          </button>
        </div>

        {/* Close Button */}
        <button className="close-overlay" onClick={onClose}>×</button>
      </div>
    </div>
  );
}

export default ProductOverlay; 